import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { FeatureFlagService } from '../services/feature-flag/feature-flag.service';

/**
 * Route guard that checks if a feature is enabled for the current tenant.
 *
 * Usage in routes:
 *   canActivate: [authGuard, featureGuard('inventory')]
 *   // or using the FEATURES constant:
 *   canActivate: [authGuard, featureGuard(FEATURES.Inventory)]
 */
export const featureGuard = (featureKey: string): CanActivateFn => {
  return () => {
    const featureFlagService = inject(FeatureFlagService);
    const router = inject(Router);

    return toObservable(featureFlagService.featuresSettled).pipe(
      filter((settled) => settled),
      take(1),
      map(() => {
        // Flags could not be loaded: do not leave the user waiting (or inside a gated route).
        if (featureFlagService.featuresError()) {
          return router.createUrlTree(['/auth/access-denied']);
        }

        if (featureFlagService.isEnabled(featureKey)) {
          return true;
        }

        return router.createUrlTree(['/']);
      }),
    );
  };
};
